import React, { useState, useRef, useEffect } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button, Divider, Stack, Alert, Chip, FormHelperText, useTheme, useMediaQuery, TextField, AlertTitle } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import { CloudUpload } from '@mui/icons-material'
import moment from 'moment'
import { useUpdateWithdrawalRequestStatusMutation } from 'src/_api/mapitShipInvoice'
import MapitFileInput from 'src/services/shared/components/inputs/MapitFileInput'
import FilePreview from './FilePreview'
import { ROLES } from 'src/constants'
import { useSelector } from 'react-redux'
import { getImageURL, joiString, runJoiValidate } from 'src/_utils'
import Joi from 'joi'
import { omit } from 'lodash'

const COD_TRANSACTION_STATUSES = {
    PENDING: 'PENDING',
    COMPLETED: 'COMPLETED',
    CANCELED: 'CANCELED'
}

export default function WithdrawalRequestDialog({
    open,
    onClose,
    transaction
}) {
    const { t } = useTranslation()
    const theme = useTheme()
    const { profile } = useSelector((state) => state.entities.profile)

    const SCHEMA = Joi.object({
        action: joiString.string().label(t("Status")).required(),
        transferApprovalFile: joiString.when('action', {
            is: 'accept',
            then: joiString.string().label(t("File")).required(),
            otherwise: Joi.optional()
        }),
        cancelReason: joiString.when('action', {
            is: 'cancel',
            then: joiString.string().label(t("Reason for Cancellation")).required(),
            otherwise: Joi.optional()
        })
    })

    const fileInputRef = useRef(null)

    const [status, setStatus] = useState()

    useEffect(() => {
        if (transaction && transaction.currentStatus !== COD_TRANSACTION_STATUSES.PENDING) {
            setStatus(transaction?.currentStatus)
        }
    }, [transaction])

    // API hooks
    const [updateStatus, {
        isLoading,
        isSuccess,
        error,
        reset
    }] = useUpdateWithdrawalRequestStatusMutation()

    const handleStatusChange = (event) => {
        setStatus(event.target.value)
    }

    const [uploadedFile, setUploadedFile] = useState('')
    const [fileError, setFileError] = useState('')
    const [cancelReason, setCancelReason] = useState('')

    const handleFileChange = ({ target: { value } }) => {
        setUploadedFile(value)
        setFileError('')
    }

    const handleFileError = (error) => {
        setFileError(error)
        setUploadedFile(null)
    }

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const [validationErrors, setValidationErrors] = useState(null)

    const handleSubmit = async () => {
        try {
            setValidationErrors(null)
            // Update status first
            let payload = {
                action: status,
                transferApprovalFile: uploadedFile,
                cancelReason: cancelReason
            }

            if (status === 'cancel') {
                payload = omit(payload, ['transferApprovalFile'])
            } else if (status === 'accept') {
                payload = omit(payload, ['cancelReason'])
            }

            const errors = runJoiValidate(SCHEMA, payload)

            if (!!errors) {
                setValidationErrors(errors)
                return
            }

            await updateStatus({ ...payload, codTransactionId: transaction?.id, }).unwrap()

            setTimeout(() => {
                onClose()
            }, 2500)

        } catch (error) {
            console.error('Error updating transaction:', error)
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case COD_TRANSACTION_STATUSES.COMPLETED:
                return 'success'
            case COD_TRANSACTION_STATUSES.CANCELED:
                return 'error'
            case COD_TRANSACTION_STATUSES.PENDING:
            default:
                return 'warning'
        }
    }

    useEffect(() => {
        // Reset state when dialog opens
        if (!open) {
            setStatus('')
            setUploadedFile('')
            setFileError('')
            setValidationErrors(null)
            reset() // Reset mutation state
        }
    }, [open, reset])

    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

    return (
        <Dialog
            open={open}
            onClose={(e) => e.preventDefault()}
            maxWidth="md"
            fullWidth
            disableRestoreFocus
            disableScrollLock
            fullScreen={fullScreen}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {t('Withdrawal Transaction Details')}
                    </Typography>
                    <Chip
                        label={t(`codTransactionStatus.${transaction?.currentStatus}`)}
                        color={getStatusColor(transaction?.currentStatus)}
                        variant="outlined"
                    />
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                {t('Merchant')}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {transaction?.merchant?.companyName || '-'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                {t('Amount')}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {!!transaction?.codAmount ? transaction?.codAmount?.toFixed(2) : '-'} {t('currency.SAR')}
                            </Typography>
                            <Typography variant="body2" color="error">
                                {t("transaction fee of 2 SAR will be deducted")}
                            </Typography>

                            <Typography variant="h6" sx={{ fontSize: "18px" }} color="error">
                                {t("The remaining amount is")}:{" "}
                                {transaction?.codAmount
                                    ? (Math.max(transaction.codAmount - 2, 0)).toFixed(2)
                                    : "-"}{" "}
                                {t("currency.SAR")}
                            </Typography>
                        </Box>
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                {t('Remaining COD Balance')}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {!!transaction?.currentCodBalance ? transaction?.currentCodBalance?.toFixed(2) : '-'} {t('currency.SAR')}
                            </Typography>
                        </Box>
                    </Grid> */}
                    {/* <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                {t('Category')}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {t(`codTransactionCategory.${transaction?.category}`)}
                            </Typography>
                        </Box>
                    </Grid> */}
                    {/* <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                {t('Created At')}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {moment(transaction?.createdAt).format('DD/MM/YYYY - h:mm A')}
                            </Typography>
                        </Box>

                    </Grid> */}
                </Grid>
                {
                    profile?.role === ROLES.admin &&
                    <>
                        <Typography variant="h5" sx={{ my: 2 }}>
                            {t('Bank Account Info')}
                        </Typography>
                        <Grid container spacing={2} >
                            <Grid item xs={12} md={6}>
                                <Stack gap={1}>
                                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                        {t("Account Holder Name")}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {transaction?.bankInformation?.accountName}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack gap={1}>
                                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                        {t("IBAN")}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {transaction?.bankInformation?.iban}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack gap={1}>
                                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                        {t("Bank")}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {transaction?.bankInformation?.bank[theme?.direction === "rtl" ? 'name_ar' : 'name_en']}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </>
                }

                {/* Status Update */}

                {(profile?.role === ROLES.admin && transaction?.currentStatus === COD_TRANSACTION_STATUSES.PENDING) &&
                    <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            {t('Update Status')}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl size='small' fullWidth>
                                    <InputLabel>{t('Status')}</InputLabel>
                                    <Select
                                        value={status}
                                        onChange={handleStatusChange}
                                        label={t('Status')}
                                    >
                                        <MenuItem value="accept">
                                            {t('codTransactionStatus.COMPLETED')}
                                        </MenuItem>
                                        <MenuItem value="cancel">
                                            {t('codTransactionStatus.CANCELED')}
                                        </MenuItem>
                                    </Select>
                                    <FormHelperText error={!!validationErrors?.action}>
                                        {validationErrors?.action}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                            {status === "cancel" &&
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        multiline
                                        rows={2}
                                        label={t("Reason for Cancellation")}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        error={!!validationErrors?.cancelReason}
                                        helperText={validationErrors?.cancelReason ?? ''}
                                    />
                                </Grid>
                            }
                            {status === "accept" &&
                                <Grid item xs={12} md={6}>
                                    <Stack>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CloudUpload />}
                                            onClick={triggerFileInput}
                                            disabled={isLoading}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {t('Transfer confirmation file')}
                                        </Button>

                                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                            {t('Supported formats: JPG, JPEG, PNG, GIF, BMP, PDF (Max 5MB)')}
                                        </Typography>
                                        <FormHelperText error={!!validationErrors?.transferApprovalFile}>
                                            {validationErrors?.transferApprovalFile}
                                        </FormHelperText>
                                    </Stack>

                                    <MapitFileInput
                                        ref={fileInputRef}
                                        name="transactionFile"
                                        accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                                        maxSize={5}
                                        onChange={handleFileChange}
                                        onError={handleFileError}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </>
                }
                {fileError && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {fileError}
                    </Typography>
                )}

                <FilePreview
                    file={!!transaction?.transferApprovalFile ? getImageURL({ image: transaction?.transferApprovalFile }) : uploadedFile}
                />
                {
                    (profile?.role !== ROLES.admin && !!transaction?.cancelReason) &&
                    <Alert variant='outlined' severity="error">
                        <AlertTitle> {t('Reason for Cancellation')}</AlertTitle>
                        {transaction?.cancelReason}
                    </Alert>
                }

            </DialogContent>
            <Divider />
            <DialogActions>
                {isSuccess && <Typography color="success.main">{t("Updated successfully")}</Typography>}
                {!!error && <Typography color="error">{t(error)}</Typography>}
                <Box mr="auto" />
                <Button
                    onClick={() => onClose(false)}
                    disabled={isLoading}
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                >
                    {t('Close')}
                </Button>

                {
                    transaction?.currentStatus === COD_TRANSACTION_STATUSES.PENDING &&
                    <LoadingButton
                        onClick={handleSubmit}
                        loading={isLoading}
                        disabled={isLoading || isSuccess}
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                    >
                        {t('Confirm')}
                    </LoadingButton>
                }
            </DialogActions>
        </Dialog>
    )
}
